import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Pause, Play } from "lucide-react";
import type { ProductSkuId } from "@limeshare/shared";
import { useUi } from "../i18n";

export function HeroStoryLeft({ onSelectSku }: { onSelectSku: (sku: ProductSkuId) => void }) {
  const { t } = useUi();
  const [slide, setSlide] = useState<0 | 1 | 2 | 3>(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (paused) return;
    const timer = setInterval(() => {
      setSlide((prev) => ((prev + 1) % 4) as 0 | 1 | 2 | 3);
    }, 5500);
    return () => clearInterval(timer);
  }, [paused]);

  const tabs = [
    { label: t.tabTeams, idx: 0 as const },
    { label: t.tabExtra, idx: 1 as const },
    { label: t.tabExceed, idx: 2 as const },
    { label: t.tabExtend, idx: 3 as const },
  ];

  return (
    <div style={{ paddingTop: 6 }}>
      {/* 4-Tab Story Progress Bars + Pause Button */}
      <div className="story-tabs">
        {tabs.map((tab) => {
          const active = slide === tab.idx;
          return (
            <button
              key={tab.idx}
              type="button"
              className={`story-tab${active ? " active" : ""}`}
              onClick={() => setSlide(tab.idx)}
            >
              <div className="story-bar-bg">
                <div
                  className="story-bar-fill"
                  style={{ width: active ? "100%" : slide > tab.idx ? "100%" : "0%" }}
                />
              </div>
              <span className="story-tab-label">{tab.label}</span>
            </button>
          );
        })}
        <button
          type="button"
          className="theme-toggle-btn"
          style={{ width: 30, height: 30 }}
          onClick={() => setPaused(!paused)}
          aria-label={paused ? "Play carousel" : "Pause carousel"}
        >
          {paused ? <Play size={13} /> : <Pause size={13} />}
        </button>
      </div>

      {/* Slide 0: Teams */}
      {slide === 0 && (
        <div>
          <span className="badge" style={{ marginBottom: 14 }}>LimeShare Teams</span>
          <div>
            <div className="avatar-stack-card">
              <div className="avatar-row">
                <span className="avatar-circle" style={{ background: "#fde68a", color: "#78350f" }}>JD</span>
                <span className="avatar-circle" style={{ background: "#fbcfe8", color: "#831843" }}>AK</span>
                <span className="avatar-circle" style={{ background: "#ddd6fe", color: "#4c1d95" }}>MR</span>
                <span className="avatar-circle" style={{ background: "#bae6fd", color: "#0c4a6e" }}>SL</span>
                <span className="avatar-circle" style={{ background: "#bbf7d0", color: "#14532d" }}>TW</span>
                <span className="avatar-circle" style={{ background: "#111812", color: "#ffffff" }}>+20</span>
              </div>
              <div style={{ fontSize: 12, color: "var(--muted)" }}>
                <s style={{ opacity: 0.7 }}>{t.perUserPricing}</s>{" "}
                <span style={{ color: "var(--lime)", fontWeight: 700 }}>{t.onePriceArrow}</span>
              </div>
            </div>
          </div>

          <h1 style={{ fontSize: "clamp(34px, 4.5vw, 52px)", fontWeight: 900, lineHeight: 1.06, margin: "0 0 14px", letterSpacing: "-0.03em" }}>
            {t.heroTeamsTitle}
          </h1>
          <p className="hint" style={{ fontSize: 16, maxWidth: 520, margin: "0 0 18px", lineHeight: 1.6 }}>
            {t.heroTeamsDesc}
          </p>
          <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 18 }}>
            {t.heroTeamsPrice}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button className="btn" style={{ padding: "14px 24px", fontSize: 15 }} onClick={() => onSelectSku("teams-25")}>
              {t.heroTeamsCta} <ArrowRight size={16} />
            </button>
            <Link to="/products/teams" className="btn ghost" style={{ padding: "14px 20px" }}>
              {t.learnMore}
            </Link>
          </div>
        </div>
      )}

      {/* Slide 1: Extra (1 TB Pro) */}
      {slide === 1 && (
        <div>
          <span className="badge" style={{ marginBottom: 14 }}>LimeShare Extra 1000</span>
          <h1 style={{ fontSize: "clamp(34px, 4.5vw, 52px)", fontWeight: 900, lineHeight: 1.06, margin: "0 0 14px", letterSpacing: "-0.03em" }}>
            {t.heroExtraTitle} <span style={{ color: "var(--lime)" }}>{t.heroExtraAccent}</span>
          </h1>
          <p className="hint" style={{ fontSize: 16, maxWidth: 520, margin: "0 0 22px", lineHeight: 1.6 }}>
            {t.heroExtraDesc}
          </p>
          <div style={{ display: "flex", gap: 12 }}>
            <button className="btn" style={{ padding: "14px 24px", fontSize: 15 }} onClick={() => onSelectSku("pro-annual")}>
              {t.heroExtraCta} <ArrowRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Slide 2: Exceed (Pay & Send 1 TB without signup) */}
      {slide === 2 && (
        <div>
          <span className="badge" style={{ marginBottom: 14 }}>LimeShare Exceed · Pay &amp; Send</span>
          <h1 style={{ fontSize: "clamp(34px, 4.5vw, 52px)", fontWeight: 900, lineHeight: 1.06, margin: "0 0 14px", letterSpacing: "-0.03em" }}>
            {t.heroExceedTitle} <span style={{ color: "var(--lime)" }}>{t.heroExceedAccent}</span>
          </h1>
          <p className="hint" style={{ fontSize: 16, maxWidth: 520, margin: "0 0 22px", lineHeight: 1.6 }}>
            {t.heroExceedDesc}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button className="btn" style={{ padding: "14px 24px", fontSize: 15 }} onClick={() => onSelectSku("exceed-200g")}>
              {t.heroExceedCta} <ArrowRight size={16} />
            </button>
            <Link to="/products/exceed" className="btn ghost" style={{ padding: "14px 20px" }}>
              {t.learnMore}
            </Link>
          </div>
        </div>
      )}

      {/* Slide 3: Extend (Keep files alive up to 1 year) */}
      {slide === 3 && (
        <div>
          <span className="badge" style={{ marginBottom: 14 }}>LimeShare Extend &amp; Rescue</span>
          <h1 style={{ fontSize: "clamp(34px, 4.5vw, 52px)", fontWeight: 900, lineHeight: 1.06, margin: "0 0 14px", letterSpacing: "-0.03em" }}>
            {t.heroExtendTitle} <span style={{ color: "var(--lime)" }}>{t.heroExtendAccent}</span>
          </h1>
          <p className="hint" style={{ fontSize: 16, maxWidth: 520, margin: "0 0 22px", lineHeight: 1.6 }}>
            {t.heroExtendDesc}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button className="btn" style={{ padding: "14px 24px", fontSize: 15 }} onClick={() => onSelectSku("extend-1y")}>
              {t.heroExtendCta} <ArrowRight size={16} />
            </button>
            <Link to="/products/extend" className="btn ghost" style={{ padding: "14px 20px" }}>
              {t.learnMore}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
